import type { SortingState } from '@tanstack/react-table';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Icon, Separator } from '~/components';
import { Spinner } from '~/components/Spinner';
import { TrackListHeader } from '~/modules/artist-roster/components/TrackList/TrackListHeader';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';
import { Breadcrumbs } from '~/modules/navigation/components/Breadcrumbs';

import { useArtistDetailsQuery } from '../../hooks/useArtistDetailsQuery';
import { ArtistDetailsHeader } from '../ArtistDetailsHeader/ArtistDetailsHeader';
import { DataError } from '../DataError/DataError';
import { TrackList } from '../TrackList/TrackList';

export function ArtistDetailsPage() {
  const { t } = useTranslation('roster');
  const { partyId } = useParams<{ partyId: string }>();
  const { trackEvent } = useAnalytics('Artist Roster');
  const {
    data: artistData,
    error,
    isLoading: isLoadingArtist,
  } = useArtistDetailsQuery({ partyId });

  const [sortingState, setSortingState] = useState<SortingState>([
    {
      id: 'initialReleaseDate',
      desc: true,
    },
  ]);
  const errorFormLink =
    'https://docs.google.com/forms/d/e/1FAIpQLSefQ1_QjxKfrYjKxX0fDwgOr8iwGy8ZH0wIAO6Gz6HlU0YvJw/viewform';

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (artistData) {
      trackEvent('artistView', {
        artist_id: artistData.partyId,
        artist_name: artistData.name,
      });
    }
  }, [trackEvent, artistData]);

  const scrollUpCallback = useCallback(() => {
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  if (!partyId || isNaN(Number(partyId))) {
    return (
      <div className="flex w-full items-center justify-start p-2xl">
        <p className="m-0 font-header text-display-xl leading-none text-default">
          {t('errors.invalidArtistId')}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Breadcrumbs.Root className="flex w-full items-center justify-start gap-sm text-body-xs">
          <Breadcrumbs.Item
            to="/roster"
            className="font-semibold underline transition text-hover hover:text-white"
          >
            {t('header.heading')}
          </Breadcrumbs.Item>
          <Breadcrumbs.Item>{partyId}</Breadcrumbs.Item>
        </Breadcrumbs.Root>
        <DataError
          title={t('errors.loadingArtist.title')}
          subtitle={
            <div className="text-center">
              <Trans
                t={t}
                i18nKey="errors.loadingArtist.subtitle"
                values={{
                  artistName: artistData?.name,
                  partyId,
                }}
                components={{
                  a: (
                    <a
                      href={errorFormLink}
                      className="underline"
                      target="_blank"
                      aria-label={t('errors.loadingArtist.aria')}
                      rel="noreferrer"
                    />
                  ),
                }}
              />
            </div>
          }
          icon={
            <Icon
              type="user-music"
              size="4x"
              variant="light"
              aria-hidden
              className="text-default"
            />
          }
        />
      </>
    );
  }

  if (isLoadingArtist || !artistData) {
    return (
      <div className="flex w-full items-center justify-center p-2xl">
        <Spinner />
      </div>
    );
  }

  return (
    <div ref={ref}>
      <Helmet>
        <title>{artistData?.name}</title>
      </Helmet>
      <div className="relative w-full">
        <div className="sticky -top-xl z-10 -mx-xl -mt-xl bg-gradient bg-[100%,auto] bg-no-repeat px-xl pt-xl bg-level-01/full">
          <div className="flex flex-col items-start justify-between gap-lg">
            <Breadcrumbs.Root className="flex w-full items-center justify-start gap-sm text-body-xs">
              <Breadcrumbs.Item
                to="/roster"
                className="font-semibold underline transition text-hover hover:text-white"
              >
                {t('header.heading')}
              </Breadcrumbs.Item>
              <Breadcrumbs.Item>{artistData?.name}</Breadcrumbs.Item>
            </Breadcrumbs.Root>
            <ArtistDetailsHeader artistData={artistData} />
            <Separator className="mx-0 my-sm w-full" />
            <TrackListHeader
              uaid={artistData.uaid}
              artistData={artistData}
              columnSort={sortingState[0]}
              scrollUpCallback={scrollUpCallback}
            />
          </div>
        </div>
        <TrackList
          uaid={artistData.uaid}
          artistData={artistData}
          columnSort={sortingState[0]}
          setSortingState={setSortingState}
        />
      </div>
    </div>
  );
}

ArtistDetailsPage.displayName = 'ArtistDetailsPage';
